import classNames from 'classnames';
import Clipboard from 'clipboard';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BrowseIcon, Download1Icon, InfoCircleIcon } from 'tdesign-icons-react';
import { Button, Loading, MessagePlugin, Popconfirm, Progress, Space, Tooltip } from 'tdesign-react';

import { CheckCircle, Copy3, EditorDel, Reload } from '@/components/icon';
import { AiVideoReq, AiVideoType, ResourceHistory, Status, UrlInfo } from '@/interface/aigc';
import { VideoPlayer } from '@/material/form-upload/core/player/video/video';
import ReviewDialog from '@/pages/library/list/review/dialog';
import { deleteAIResource } from '@/services/aigc';
import { CgiErrorCode } from '@/services/cgi';
import { downloadVideoWatermark } from '@/utils/downloadFile';

import styles from './index.module.less';

export interface HistoryItemProps {
  history: ResourceHistory;
  selectedVideo: string;
  isHorizontal: boolean;
  generateVideo: (postData: AiVideoReq) => void;
  handleSelect: (url: UrlInfo) => void;
  onDelete: (id: string) => void;
}

const VideoHistoryItem: React.FC<HistoryItemProps> = ({
  history,
  selectedVideo,
  isHorizontal,
  generateVideo,
  handleSelect,
  onDelete,
}) => {
  const { status: historyStatus, urls, prompt, ratio, relateId, aiTxt2ImgReq = '' } = history;
  const loading = (historyStatus === Status.WAITING
    && !urls.some(e => (e.url && e.url !== 'initUrl') || e.errMsg));

  const [progress, setProgress] = useState<number>(0);
  const [reviewVisible, setReviewVisible] = useState<boolean>(false);
  const isValid = (url: string) => url && url !== 'initUrl';
  const isSelected = (url: string) => isValid(url) && url === selectedVideo;

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (loading) {
      setProgress(0);
      interval = setInterval(() => {
        setProgress((prev) => {
          const random = Math.random();
          if (prev < 35) {
            return Math.min(prev + 7 + Math.floor(random * 7), 35);
          } if (prev < 65) {
            return Math.min(prev + 5 + Math.floor(random * 5), 65);
          } if (prev < 90) {
            return Math.min(prev + 3 + Math.floor(random * 3), 90);
          } if (prev < 99) {
            return Math.min(prev + Math.floor(random * 2), 99);
          }
          return prev;
        });
      }, 2000);
    } else {
      setProgress(100);
    }
    return () => clearInterval(interval);
  }, [loading]);

  const aiVideoReq: AiVideoReq = useMemo(() => {
    try {
      return JSON.parse(aiTxt2ImgReq);
    } catch {
      return {
        type: AiVideoType.IMAGE,
        content: {
          text: prompt,
          ratio: isHorizontal ? '16:9' : '9:16',
        },
      };
    }
  }, [aiTxt2ImgReq, prompt, isHorizontal]);

  const handleRegenerate = () => {
    generateVideo({ ...aiVideoReq });
  };

  const handleDelete = async () => {
    const res = await deleteAIResource({ relateIds: [relateId] });
    if (res.code !== CgiErrorCode.NO_ERROR || !res.data.hasDeleted) {
      MessagePlugin.error('删除失败，请稍后重试');
      return;
    }
    MessagePlugin.success('删除成功');
    onDelete(relateId);
  };

  const clickVideo = (urlInfo: UrlInfo) => {
    const { url } = urlInfo;
    if (isValid(url)) {
      handleSelect(urlInfo);
    }
  };

  const copyBtnRef = useRef(null);

  useEffect(() => {
    if (copyBtnRef.current) {
      const clipboard = new Clipboard(copyBtnRef.current, {
        text: () => prompt,
      });

      clipboard.on('success', () => {
        MessagePlugin.success('已复制到剪贴板');
      });

      clipboard.on('error', () => {
        MessagePlugin.error('复制失败');
      });

      return () => {
        clipboard.destroy();
      };
    }
  }, [prompt]);

  const mask = (
    <div
      style={{
        background: 'rgba(0,0,0,.5)',
        color: '#fff',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        position: 'absolute',
        width: '100%',
        cursor: 'pointer',
      }}
    >
      <BrowseIcon size="12px" name={'browse'} />
    </div>
  );

  const openReview = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setReviewVisible(true);
  };

  return (
    <div
      className={classNames(
        styles.container,
        { [styles.selected]: isSelected(urls?.[0]?.url) },
      )}
      style={{ height: ratio === '16:9' ? 154 : 272 }}
      onClick={() => clickVideo(urls?.[0])}
    >
      <div className={styles.left}>
        <Loading
          showOverlay
          loading={loading}
          // text="生成中，您也可以离开该页面稍后再来查看"
          style={{ width: '100%' }}
          className={styles.customLoading}
          indicator={
            <Progress
              percentage={progress}
              theme={'circle'}
              size={60}
            />}
        >
          <Space
            size={10}
            align='center'
            style={{ display: 'flex', width: '100%', height: '100%' }}
            className={styles.customSpace}
          >
            {urls.map((urlInfo, index) => {
              const { url, errMsg: err } = urlInfo;
              const [downloadLoading, setDownloadLoading] = useState<boolean>(false);

              const handleDownload = useCallback(async (e: React.MouseEvent<HTMLElement>) => {
                e.stopPropagation();
                if (!url || downloadLoading) return;
                setDownloadLoading(true);
                try {
                  await downloadVideoWatermark(url, ratio === '16:9');
                } catch (error) {
                  console.error(error);
                  MessagePlugin.error('下载失败，请稍后重试');
                } finally {
                  setDownloadLoading(false);
                }
              }, [url, downloadLoading]);

              return (
                <Loading
                  key={index}
                  showOverlay
                  loading={!url && !err && !loading && historyStatus !== Status.FINISHED}
                  style={{ display: 'flex', flex: 1, height: '100%' }}
                >
                  <Tooltip content={err}>
                    <div
                      key={index}
                      className={styles.video}
                    >
                      {(historyStatus === Status.FINISHED || err) && !isValid(url)
                        ? <div className={styles.fail}>
                          <InfoCircleIcon />
                        </div>
                        : <>
                          <VideoPlayer url={url} />
                          <Button
                            variant='text'
                            className={styles.download}
                            onClick={(e: React.MouseEvent<HTMLElement>) => handleDownload(e)}
                            loading={downloadLoading}
                          >
                            {!downloadLoading && <Download1Icon
                              color={'#FFFFFFE6'}
                            />}
                          </Button>
                          {isValid(url) && <div
                            className={styles.review}
                            onClick={(e: React.MouseEvent<HTMLElement>) => openReview(e)}
                          >
                            {mask}
                          </div>}
                          <ReviewDialog
                            visible={reviewVisible}
                            onClose={() => setReviewVisible(false)}
                            dialogConfig={{
                              onOverlayClick: (context: { e: React.MouseEvent<HTMLDivElement> }) => {
                                context.e.stopPropagation();
                                setReviewVisible(false);
                              },
                            }}
                          >
                            <VideoPlayer
                              url={url}
                              wrapWidth={800}
                              wrapHeight={800}
                              controls={true}
                              controlsList='nodownload'
                              hiddenPlayBtn={true}
                              wrapStyle={{ background: 'black' }}
                            />
                          </ReviewDialog>
                        </>}
                      {isSelected(url) && <CheckCircle className={styles.icon} />}
                    </div>
                  </Tooltip>
                </Loading>
              );
            })}
          </Space>
        </Loading>
      </div>
      <div className={styles.right}>
        <div className={styles.prompt}>{prompt}</div>
        <div
          className={styles.buttons}
          onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
        >
          <Button
            theme="default"
            className={styles.btn}
            onClick={handleRegenerate}
          >
            <div className={styles.text}>
              <Reload />重新生成
            </div>
          </Button>
          <Button
            theme="default"
            className={styles.btn}
            ref={copyBtnRef}
          >
            <div className={styles.text}>
              <Copy3 />复制描述
            </div>
          </Button>
          <Popconfirm
            content={'删除生成记录'}
            onConfirm={handleDelete}
          >
            <Button
              theme="default"
              className={styles.delBtn}
              disabled={historyStatus !== Status.FINISHED}
            >
              <div className={styles.text}>
                <EditorDel />
              </div>
            </Button>
          </Popconfirm>
        </div>
      </div>
    </div>
  );
};

export default VideoHistoryItem;
